import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import "./styles/CustomCheckout.css";
import { trackPromoCodeUsage, trackCheckoutEvent, getUserSegment } from "../../services/analyticsService";

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY as string
);

// The checkout form component
const CheckoutForm: React.FC<{
  customerId: string;
  promoCode?: string;
  userEmail: string;
}> = ({ customerId, promoCode: initialPromoCode, userEmail }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { getAccessTokenSilently } = useAuth0(); // Get Auth0 token function
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Initialize email from Auth0 user data
  const [email, setEmail] = useState(userEmail || "");

  // State for user-entered promo code
  const [promoCode, setPromoCode] = useState(initialPromoCode || "");

  // Make sure email field is updated when userEmail prop changes
  useEffect(() => {
    if (userEmail && userEmail !== email) {
      setEmail(userEmail);
    }
  }, [userEmail, email]);

  // Initialize promo code from props
  useEffect(() => {
    if (initialPromoCode && initialPromoCode !== promoCode) {
      setPromoCode(initialPromoCode);
    }
  }, [initialPromoCode, promoCode]);

  // Get current date
  const today = new Date();

  // State to track promo code verification details
  const [promoCodeData, setPromoCodeData] = useState({
    isValid: false,
    trialDays: 30,
    description: "",
    isVerifying: false,
  });

  // Calculate trial period based on verified promo code
  const trialDays = promoCodeData.isValid ? promoCodeData.trialDays : 30;
  const trialEndDate = new Date(today);
  trialEndDate.setDate(today.getDate() + trialDays);

  // Verify promo code with backend when it changes
  useEffect(() => {
    // Skip if no promo code
    if (!promoCode) {
      setPromoCodeData((prev) => ({
        ...prev,
        isValid: false,
        trialDays: 30,
        description: "",
      }));
      return;
    }

    const verifyPromoCode = async () => {
      setPromoCodeData((prev) => ({ ...prev, isVerifying: true }));
      try {
        // Get authentication token from Auth0
        const token = await getAccessTokenSilently();

        // Call backend API to verify promo code with authentication
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/payments/verify-promo-code`,
          { promo_code: promoCode },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Update state with detailed promo code info from backend
        const promoData = {
          isValid: response.data.valid,
          trialDays: response.data.benefits?.trialDays || 30,
          description:
            response.data.benefits?.description || response.data.message || "",
          isVerifying: false,
        };
        setPromoCodeData(promoData);
        
        // Track promo code usage in analytics
        trackPromoCodeUsage(
          promoCode, 
          promoData.isValid, 
          promoData.trialDays
        );
      } catch (error) {
        console.error("Error verifying promo code:", error);
        setPromoCodeData({
          isValid: false,
          trialDays: 30,
          description: "",
          isVerifying: false,
        });
      }
    };

    verifyPromoCode();
  }, [promoCode, getAccessTokenSilently]);

  // Format the date as Month Day, Year
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Convert days to months text for display
  const daysToMonthsText = (days: number) => {
    if (days < 40) return "for 1 month";
    if (days < 70) return "for 2 months";
    if (days < 100) return "for 3 months";
    if (days < 200) return "for 6 months";
    return `for ${Math.round(days / 30)} months`;
  };

  // Get the trial period text based on promo code
  const trialPeriodText = promoCodeData.isValid
    ? daysToMonthsText(promoCodeData.trialDays)
    : "for 1 month";

  // State for multi-step form
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3; // Total number of steps in the checkout flow

  // Handle next step navigation
  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);

      // Add step class to parent container
      const formContainer = document.querySelector(".form-container");
      if (formContainer) {
        formContainer.classList.remove(
          "checkout-step-1",
          "checkout-step-2",
          "checkout-step-3"
        );
        formContainer.classList.add(`checkout-step-${currentStep + 1}`);
      }
    }
  };

  // Handle previous step navigation
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);

      // Add step class to parent container
      const formContainer = document.querySelector(".form-container");
      if (formContainer) {
        formContainer.classList.remove(
          "checkout-step-1",
          "checkout-step-2",
          "checkout-step-3"
        );
        formContainer.classList.add(`checkout-step-${currentStep - 1}`);
      }
    }
  };

  // Set the step class on the form container when component mounts or step changes
  useEffect(() => {
    // Add step class to parent container
    const formContainer = document.querySelector(".form-container");
    if (formContainer) {
      formContainer.classList.remove(
        "checkout-step-1",
        "checkout-step-2",
        "checkout-step-3"
      );
      formContainer.classList.add(`checkout-step-${currentStep}`);
    }
  }, [currentStep]);

  // Calculate progress for the progress bar
  const progressWidth = ((currentStep - 1) / (totalSteps - 1)) * 100;

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");
    
    // Track checkout begin event
    const userSegment = getUserSegment({ promo_code: promoCode });
    trackCheckoutEvent('begin_checkout', 0, promoCode, userSegment);

    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error("Card element not found");
      }

      // Create payment method
      const { error: createError, paymentMethod: method } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });

      if (createError) {
        throw new Error(createError.message);
      }

      if (!method) {
        throw new Error("Payment method creation failed");
      }

      // Get authentication token from Auth0
      const token = await getAccessTokenSilently();

      // Create subscription on your backend using the new endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/payments/create-subscription`,
        {
          customer: customerId,
          payment_method_id: method.id,
          email,
          promo_code: promoCode,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Check if subscription exists and has an ID (using new response format)
      if (response.data.subscriptionId) {
        console.log(
          "Subscription created successfully:",
          response.data.subscriptionId
        );

        // Confirm the payment intent if needed (legacy format check)
        const { latestInvoice } = response.data;
        if (latestInvoice?.paymentIntent) {
          const { error: confirmError } = await stripe.confirmCardPayment(
            latestInvoice.paymentIntent.clientSecret
          );

          if (confirmError) {
            throw new Error(confirmError.message);
          }
        }

        // Payment succeeded
        setIsProcessing(false);
        
        // Track successful purchase
        trackCheckoutEvent('purchase', 0, promoCode, userSegment);

        // Redirect to browse page with welcome parameter
        window.location.href = "/browse?welcome_premium=true";
      } else {
        // Handle API error - no subscription ID in response
        throw new Error(response.data.message || "Error creating subscription");
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="premium-checkout-form">
      <h1 className="checkout-title">Checkout</h1>

      {/* Step progress indicator */}
      <div className="checkout-steps">
        <div className="step-line">
          <div
            className="step-line-progress"
            style={{ width: `${progressWidth}%` }}
          ></div>
        </div>

        <div
          className={`checkout-step ${currentStep >= 1 ? "step-active" : ""} ${
            currentStep > 1 ? "step-completed" : ""
          }`}
        >
          <div className="step-number">{currentStep <= 1 && "1"}</div>
          <div className="step-label">Account</div>
        </div>

        <div
          className={`checkout-step ${currentStep >= 2 ? "step-active" : ""} ${
            currentStep > 2 ? "step-completed" : ""
          }`}
        >
          <div className="step-number">{currentStep <= 2 && "2"}</div>
          <div className="step-label">Payment</div>
        </div>

        <div
          className={`checkout-step ${currentStep >= 3 ? "step-active" : ""}`}
        >
          <div className="step-number">3</div>
          <div className="step-label">Confirm</div>
        </div>
      </div>

      {/* Step 1: Account Details */}
      <div
        className={`checkout-step-content ${currentStep === 1 ? "active" : ""}`}
      >
        <div className="checkout-account-details">
          <div className="account-info">
            <p className="account-name">1 Premium account</p>
          </div>
          <div className="price-info">
            <p className="price-amount">A$0.00</p>
            <p className="price-period">{trialPeriodText}</p>
          </div>
        </div>

        <hr className="checkout-divider" />

        <div className="checkout-timeline">
          <div className="timeline-container">
            <div className="timeline-connector">
              <div className="timeline-dot filled"></div>
              <div className="timeline-line"></div>
              <div className="timeline-dot"></div>
            </div>
            <div className="timeline-texts">
              <p className="timeline-text">Today: 7 days for A$0.00</p>
              <p className="timeline-text second">
                Starting on {formatDate(trialEndDate)}: A$2.95
              </p>
            </div>
          </div>
          {promoCode && (
            <div className="promo-status">
              {promoCodeData.isVerifying ? (
                <p className="promo-verifying">Verifying promo code...</p>
              ) : promoCodeData.isValid ? (
                <p className="promo-valid">
                  Promo code applied: {promoCodeData.trialDays} days free trial
                  {promoCodeData.description && (
                    <span> - {promoCodeData.description}</span>
                  )}
                </p>
              ) : (
                <p className="promo-invalid">Invalid promo code</p>
              )}
            </div>
          )}
          <p className="cancel-notice">
            • Cancel anytime online.{" "}
            <a
              target="_blank"
              href="/sign-up-terms-of-use"
              className="terms-link"
            >
              Terms apply
            </a>
          </p>
        </div>

        <div className="checkout-input-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            readOnly
            placeholder="you@example.com"
            className="readonly-email"
            required
          />
        </div>

        <div className="step-buttons">
          <div></div> {/* Empty div to maintain space-between alignment */}
          <button type="button" className="btn-next" onClick={handleNextStep}>
            Continue to Payment
          </button>
        </div>
      </div>

      {/* Step 2: Payment Method */}
      <div
        className={`checkout-step-content ${currentStep === 2 ? "active" : ""}`}
      >
        <div className="checkout-account-details">
          <div className="account-info">
            <p className="account-name">1 Premium account</p>
          </div>
          <div className="price-info">
            <p className="price-amount">A$0.00</p>
            <p className="price-period">{trialPeriodText}</p>
          </div>
        </div>

        <hr className="checkout-divider" />

        <div className="payment-method-section">
          <p className="payment-method-title">Payment method</p>

          <fieldset className="payment-methods">
            <label
              className="payment-method selected"
              aria-label="Credit or debit card payment method"
            >
              <input
                type="radio"
                name="paymentMethod"
                checked={true}
                readOnly
              />
              <div className="payment-method-content">
                <span className="payment-label">Credit or debit card</span>
                <div className="card-icons">
                  <img
                    src="https://js.stripe.com/v3/fingerprinted/img/visa-365725566f9578a9589553aa9296d178.svg"
                    alt="Visa"
                    className="card-icon"
                  />
                  <img
                    src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg"
                    alt="Mastercard"
                    className="card-icon"
                  />
                  <img
                    src="https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg"
                    alt="American Express"
                    className="card-icon"
                  />
                  <img
                    src="https://js.stripe.com/v3/fingerprinted/img/unionpay-8a10aefc7295216c338ba4e1224627a1.svg"
                    alt="UnionPay"
                    className="card-icon"
                  />
                </div>
              </div>
            </label>
          </fieldset>

          <div className="card-details">
            <label htmlFor="card-element" style={srOnlyStyle}>
              Credit Card
            </label>
            <CardElement
              id="card-element"
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
                hidePostalCode: true,
              }}
            />
          </div>
        </div>

        <div className="step-buttons">
          <button type="button" className="btn-prev" onClick={handlePrevStep}>
            Back
          </button>
          <button type="button" className="btn-next" onClick={handleNextStep}>
            Continue to Confirm
          </button>
        </div>
      </div>

      {/* Step 3: Summary and Confirmation */}
      <div
        className={`checkout-step-content ${currentStep === 3 ? "active" : ""}`}
      >
        <h2 className="summary-heading">Summary</h2>
        <div className="summary-section">
          <div className="summary-timeline">
            <div className="timeline-container">
              <div className="timeline-connector">
                <div className="timeline-dot filled"></div>
                <div className="timeline-line"></div>
                <div className="timeline-dot"></div>
              </div>
              <div className="timeline-texts">
                <p className="timeline-text">
                  Today:{" "}
                  {promoCodeData.isValid
                    ? `${promoCodeData.trialDays} days`
                    : "1 month"}{" "}
                  for A$0.00
                </p>
                <p className="timeline-text second">
                  Starting on {formatDate(trialEndDate)}: A$2.95
                </p>
              </div>
            </div>
          </div>

          <div className="promo-code-container">
            <div className="promo-input-wrapper">
              <input
                id="promo-code"
                type="text"
                className="promo-input"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter promo code"
              />
            </div>
            {promoCodeData.isVerifying && (
              <p className="promo-status verifying">Verifying...</p>
            )}
            {promoCode && !promoCodeData.isVerifying && (
              <p
                className={`promo-status ${
                  promoCodeData.isValid ? "valid" : "invalid"
                }`}
              >
                {promoCodeData.isValid
                  ? `Valid: ${promoCodeData.description}`
                  : "Invalid promo code"}
              </p>
            )}
          </div>

          <div className="total-now">
            <h3>Total Now</h3>
            <p className="total-price">A$0.00</p>
          </div>
        </div>

        <div className="terms-section">
          <p>
            You consent to get access to your Grocerize Premium immediately, and
            that if you don't cancel before the trial ends on{" "}
            {formatDate(trialEndDate)}, you will automatically be charged the
            subscription fee every month until you cancel. If you cancel during
            your trial period, you will lose access to your Premium benefits
            immediately. You may cancel at any time, however you will not be
            entitled to a refund on cancellation. Full terms and instructions on
            how to cancel are{" "}
            <a
              target="_blank"
              href="/sign-up-terms-of-use"
              className="terms-link"
            >
              available here
            </a>
            .
          </p>
        </div>

        {errorMessage && <div className="error-message">{errorMessage}</div>}

        <div className="step-buttons">
          <button type="button" className="btn-prev" onClick={handlePrevStep}>
            Back
          </button>
          <button
            className="btn-submit"
            type="submit"
            disabled={!stripe || isProcessing}
          >
            {isProcessing ? "Processing..." : "Complete Purchase"}
          </button>
        </div>
      </div>
    </form>
  );
};

// Add screen reader only style for accessibility
const srOnlyStyle = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: "0",
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: "0",
} as React.CSSProperties;

// Interface for CustomCheckout props
interface CustomCheckoutProps {
  promoCode?: string;
}

// Main component that wraps the checkout form with Stripe Elements
const CustomCheckout: React.FC<CustomCheckoutProps> = ({ promoCode = "" }) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error] = useState<string | null>(null); // Error state for displaying errors

  useEffect(() => {
    // Wait for Auth0 to load
    if (isLoading) return;

    // If not authenticated, user will be redirected by ProtectedRoute
    if (!isAuthenticated || !user) return;

    // Once we have user data from Auth0, we're ready to show the checkout
    setIsDataLoading(false);

    // Log authentication status
    console.log("User authenticated for checkout:", user.email);
  }, [isLoading, isAuthenticated, user]);

  // Extract customer ID from Auth0 user
  const customerId = user?.sub || "";

  // Get final promo code - either from props (for modal) or URL params (for standalone page)
  const finalPromoCode =
    promoCode ||
    (() => {
      const params = new URLSearchParams(window.location.search);
      return params.get("promo_code") || "";
    })();

  if (isLoading || isDataLoading) {
    return (
      <div className="custom-checkout-container">
        <div className="loading-container">
          <p>Loading checkout information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="custom-checkout-container">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-checkout-container">
      <Elements stripe={stripePromise}>
        <CheckoutForm
          customerId={customerId}
          promoCode={finalPromoCode}
          userEmail={user?.email || ""}
        />
      </Elements>
    </div>
  );
};

export default CustomCheckout;
